// Small inline LCS-based line diff. ~50 lines, zero deps.
//
// Why inline instead of pulling in `diff` from npm: the package
// manager (pnpm) isn't available in the host environment for adding
// new dependencies right now, and we only need a basic line-level
// diff for Phase 1 of the revision-diff feature. If we later want
// fancier word/char-level diffs or move/rename heuristics, swap to
// jsdiff (`diff` package) here.
//
// Output is a flat list of hunks: each is either `equal`, `add`, or
// `remove`. The renderer turns them into a side-by-side or unified
// diff view.

export type DiffLineKind = 'equal' | 'add' | 'remove';

export interface DiffLine {
  kind: DiffLineKind;
  // 1-based line numbers in the original (left) and modified (right)
  // sides. Null on the side where the line doesn't exist.
  leftNo: number | null;
  rightNo: number | null;
  text: string;
}

export function diffLines(left: string, right: string): DiffLine[] {
  const a = left.split('\n');
  const b = right.split('\n');
  // Strip a single trailing empty line that the `split` produces for
  // files ending in `\n` — otherwise every diff looks like it added
  // a blank line at the end.
  if (a.length > 0 && a[a.length - 1] === '') a.pop();
  if (b.length > 0 && b[b.length - 1] === '') b.pop();

  const n = a.length;
  const m = b.length;

  // Standard LCS DP table. Memory is O(n*m); fine up to a few thousand
  // lines on each side. Files in the depot are well under that bound
  // (.uefnproject, .verse source, etc.).
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) {
        dp[i]![j] = dp[i + 1]![j + 1]! + 1;
      } else {
        dp[i]![j] = Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
      }
    }
  }

  // Walk the table to produce the diff sequence. At each step,
  // prefer equal (consume both), then either side that the DP says
  // is longer.
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'equal', leftNo: i + 1, rightNo: j + 1, text: a[i]! });
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      out.push({ kind: 'remove', leftNo: i + 1, rightNo: null, text: a[i]! });
      i++;
    } else {
      out.push({ kind: 'add', leftNo: null, rightNo: j + 1, text: b[j]! });
      j++;
    }
  }
  while (i < n) {
    out.push({ kind: 'remove', leftNo: i + 1, rightNo: null, text: a[i]! });
    i++;
  }
  while (j < m) {
    out.push({ kind: 'add', leftNo: null, rightNo: j + 1, text: b[j]! });
    j++;
  }
  return out;
}

// Collapse long runs of equal lines into context-only chunks around
// changes. GitHub-style 3-line context — anything else is folded
// into a single "… N unchanged lines …" marker.
export interface DiffHunk {
  // Continuous run of `equal` lines that are fully contained between
  // two change groups (not at file edges). When folded, the renderer
  // shows a "Show N unchanged lines" expander instead of the lines
  // themselves. The same shape carries change groups too — `folded`
  // is false for those.
  lines: DiffLine[];
  folded: boolean;
}

export function foldDiffContext(lines: DiffLine[], context = 3): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  let i = 0;
  const isChange = (l: DiffLine): boolean => l.kind !== 'equal';
  while (i < lines.length) {
    // Find next change boundary.
    const j = i;
    if (!isChange(lines[j]!)) {
      // Run of equal lines starting at i. Look ahead for the first
      // change after this run.
      let k = i;
      while (k < lines.length && !isChange(lines[k]!)) k++;
      // The equal run is lines[i..k). Decide how much to fold.
      const isStart = i === 0;
      const isEnd = k === lines.length;
      const leadingContext = isStart ? 0 : context;
      const trailingContext = isEnd ? 0 : context;
      const runLen = k - i;
      if (runLen <= leadingContext + trailingContext) {
        hunks.push({ lines: lines.slice(i, k), folded: false });
      } else {
        if (leadingContext > 0) {
          hunks.push({ lines: lines.slice(i, i + leadingContext), folded: false });
        }
        hunks.push({
          lines: lines.slice(i + leadingContext, k - trailingContext),
          folded: true,
        });
        if (trailingContext > 0) {
          hunks.push({ lines: lines.slice(k - trailingContext, k), folded: false });
        }
      }
      i = k;
    } else {
      // Run of change lines. Keep all of them as one hunk.
      let k = i;
      while (k < lines.length && isChange(lines[k]!)) k++;
      hunks.push({ lines: lines.slice(i, k), folded: false });
      i = k;
    }
  }
  return hunks;
}
